import React, { useLayoutEffect, useRef, useState } from "react";
import { Router, UNSAFE_createBrowserHistory as createBrowserHistory } from "react-router-dom";

/**
 * Drop-in BrowserRouter that does NOT wrap location updates in
 * React.startTransition.
 *
 * React Router v7's BrowserRouter always does:
 *   startTransition(() => setState(newLocation))
 * That updates the browser URL immediately, but React keeps painting the
 * previously committed page until the transition finishes. On heavy pages
 * (Dynamic Analytics queue loads, lazy chunks, etc.) the transition can
 * stall indefinitely — tabs and sidebar clicks change the URL with no UI
 * change until a full refresh.
 *
 * Committing location state synchronously keeps the UI in lockstep with the
 * URL. Suspense fallbacks still work for lazy routes because we are no
 * longer inside a transition that suppresses them.
 */
export default function SyncBrowserRouter({ basename, children, window: windowProp }) {
  const historyRef = useRef(null);
  if (historyRef.current == null) {
    historyRef.current = createBrowserHistory({
      window: windowProp,
      v5Compat: true,
    });
  }
  const history = historyRef.current;

  const [state, setState] = useState({
    action: history.action,
    location: history.location,
  });

  useLayoutEffect(() => history.listen(setState), [history]);

  return (
    <Router
      basename={basename}
      location={state.location}
      navigationType={state.action}
      navigator={history}
    >
      {children}
    </Router>
  );
}
